import React from "react";
import { Box, IconButton, Typography, Theme } from "@mui/material";
import { Mic, X, Pause, Check } from "lucide-react";
import AudioWaveform from "./AudioWaveform";
import { formatTime } from "@/modules/common/utils/audio.utils";

interface RecordingControlsProps {
  isPaused: boolean;
  recordingTime: number;
  audioLevels: number[];
  onCancel: () => void;
  onPauseResume: () => void;
  onConfirm: () => void;
  theme: Theme;
}

const RecordingControls: React.FC<RecordingControlsProps> = ({
  isPaused,
  recordingTime,
  audioLevels,
  onCancel,
  onPauseResume,
  onConfirm,
  theme,
}) => (
  <Box
    sx={{
      mb: theme.nawy.spacing.xs,
      minHeight: 120,
      display: "flex",
      borderRadius: theme.nawy.radius.lg,
      border: "1px solid",
      flexDirection: "column",
      borderColor: theme.nawy.colors.semantic.border.primary,
      backgroundColor: theme.nawy.colors.semantic.layer.primary,
    }}
  >
    <Box
      sx={{
        paddingTop: theme.nawy.spacing.xs,
        paddingBottom: theme.nawy.spacing.xs,
        gap: theme.nawy.spacing.xs,
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Mic size={16} color={theme.nawy.colors.semantic.text.tertiary} />
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.sm.regular,
          color: theme.nawy.colors.semantic.text.tertiary,
        }}
      >
        Recording an audio clip....
      </Typography>
    </Box>
    <Box
      sx={{
        px: theme.nawy.spacing.sm,
        pb: theme.nawy.spacing.sm,
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
      }}
    >
      <Box sx={{ justifySelf: "start" }}>
        <IconButton
          size="small"
          onClick={onCancel}
          sx={{
            width: 32,
            height: 32,
            border: "1px solid",
            borderRadius: theme.nawy.radius.full,
            color: theme.nawy.colors.semantic.text.secondary,
            borderColor: theme.nawy.colors.semantic.border.primary,
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.secondary,
            },
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      <AudioWaveform
        gap={theme.nawy.spacing.xxxs}
        height={24}
        barWidth={2}
        isPaused={isPaused}
        levels={audioLevels.slice(-50)}
        sx={{ justifyContent: "center", justifySelf: "center" }}
        activeColor={theme.nawy.colors.semantic.border.accent}
        inactiveColor={theme.nawy.colors.semantic.border.accent}
      />

      <Box
        sx={{
          justifySelf: "end",
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.sm,
        }}
      >
        <Typography
          sx={{
            minWidth: 40,
            textAlign: "center",
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          {formatTime(recordingTime)}
        </Typography>

        <IconButton
          size="small"
          onClick={onPauseResume}
          sx={{
            width: 32,
            height: 32,
            borderRadius: theme.nawy.radius.full,
            color: theme.nawy.colors.semantic.layer.accent,
            backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.border.accent,
              color: theme.nawy.colors.semantic.layer.primary,
            },
          }}
        >
          {isPaused ? <Mic size={16} /> : <Pause size={16} />}
        </IconButton>

        <IconButton
          size="small"
          onClick={onConfirm}
          disabled={recordingTime < 1}
          sx={{
            width: 32,
            height: 32,
            borderRadius: theme.nawy.radius.full,
            color: theme.nawy.colors.semantic.layer.primary,
            backgroundColor: theme.nawy.colors.semantic.layer.accent,
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.accentDark,
            },
            "&:disabled": {
              backgroundColor:
                theme.nawy.colors.semantic.layer.extension.disabledLight,
              color: theme.nawy.colors.semantic.text.extension.disabled,
            },
          }}
        >
          <Check size={16} />
        </IconButton>
      </Box>
    </Box>
  </Box>
);

export default RecordingControls;
